import { useEffect, useState } from 'react';
import { Modal } from '@mui/material';

// Default menu options / modules list
const defaultMenuOptions = [
  'Dashboard',
  'Candidates',
  'Sales',
  'Job Leads',
  'Projects',
  'Interviews',
  'Technologies',
  'Vendors',
  'Sales Team',
  'Training',
  'Classes',
  'Faculty',
  'Curriculum',
  'Trainings',
  'Account',
  'Users',
  'User Roles',
];

// Default functionality access controls
const defaultFunctionalityOptions = [
  'Dashboard',
  'Candidates',
  'Sales',
  'Job Leads',
  'Projects',
  'Interviews',
  'Technologies',
  'Vendors',
  'Sales Team',
  'Training',
  'Classes',
  'Faculty',
  'Curriculum',
  'Trainings',
  'Account',
  'Users',
  'User Roles',
];

const PermissionsModal = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode,
  defaultValues,
  roleOptions = [],
  isSubmitting,
}) => {
  const [selectedRole, setSelectedRole] = useState('');
  const [menuPermissions, setMenuPermissions] = useState({});
  const [functionalityPermissions, setFunctionalityPermissions] = useState({});
  const [newMenuOption, setNewMenuOption] = useState('');
  const [newFunctionalityOption, setNewFunctionalityOption] = useState('');
  const [menuOptionsList, setMenuOptionsList] = useState([...defaultMenuOptions]);
  const [functionalityList, setFunctionalityList] = useState([...defaultFunctionalityOptions]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (defaultValues && isEditMode) {
        setSelectedRole(defaultValues.role_id || defaultValues.unique_id || '');
        // Parse existing permissions if available
        if (defaultValues.access_json) {
          try {
            const parsed = typeof defaultValues.access_json === 'string'
              ? JSON.parse(defaultValues.access_json)
              : defaultValues.access_json;
            setMenuPermissions(parsed.menuPermissions || {});
            setFunctionalityPermissions(parsed.functionalityPermissions || {});
            // Add any custom options from saved data
            if (parsed.customMenuOptions) {
              setMenuOptionsList([...defaultMenuOptions, ...parsed.customMenuOptions]);
            }
            if (parsed.customFunctionalityOptions) {
              setFunctionalityList([...defaultFunctionalityOptions, ...parsed.customFunctionalityOptions]);
            }
          } catch (e) {
            console.warn('Could not parse access_json');
            setMenuPermissions({});
            setFunctionalityPermissions({});
          }
        } else {
          setMenuPermissions({});
          setFunctionalityPermissions({});
        }
      } else {
        setSelectedRole('');
        setMenuPermissions({});
        setFunctionalityPermissions({});
        setMenuOptionsList([...defaultMenuOptions]);
        setFunctionalityList([...defaultFunctionalityOptions]);
      }
      setNewMenuOption('');
      setNewFunctionalityOption('');
    }
  }, [isOpen, defaultValues, isEditMode]);

  // Load permissions when role is selected
  useEffect(() => {
    if (selectedRole && roleOptions.length > 0) {
      const role = roleOptions.find(
        (r) => r.role_id === selectedRole || r.unique_id === selectedRole || r.role_id === Number(selectedRole)
      );
      if (role && role.access_json) {
        try {
          const parsed = typeof role.access_json === 'string'
            ? JSON.parse(role.access_json)
            : role.access_json;
          setMenuPermissions(parsed.menuPermissions || {});
          setFunctionalityPermissions(parsed.functionalityPermissions || {});
          if (parsed.customMenuOptions) {
            setMenuOptionsList([...defaultMenuOptions, ...parsed.customMenuOptions]);
          }
          if (parsed.customFunctionalityOptions) {
            setFunctionalityList([...defaultFunctionalityOptions, ...parsed.customFunctionalityOptions]);
          }
        } catch (e) {
          console.warn('Could not parse role access_json');
        }
      } else {
        // Reset permissions when selecting a new role without saved permissions
        setMenuPermissions({});
        setFunctionalityPermissions({});
      }
    }
  }, [selectedRole, roleOptions]);

  // Toggle menu permission
  const toggleMenuPermission = (item) => {
    setMenuPermissions((prev) => ({
      ...prev,
      [item]: !prev[item],
    }));
  };

  // Toggle functionality permission
  const toggleFunctionalityPermission = (item) => {
    setFunctionalityPermissions((prev) => ({
      ...prev,
      [item]: !prev[item],
    }));
  };

  // Add new menu option
  const handleAddMenuOption = () => {
    if (newMenuOption.trim() && !menuOptionsList.includes(newMenuOption.trim())) {
      setMenuOptionsList((prev) => [...prev, newMenuOption.trim()]);
      setNewMenuOption('');
    }
  };

  // Add new functionality option
  const handleAddFunctionalityOption = () => {
    if (newFunctionalityOption.trim() && !functionalityList.includes(newFunctionalityOption.trim())) {
      setFunctionalityList((prev) => [...prev, newFunctionalityOption.trim()]);
      setNewFunctionalityOption('');
    }
  };

  const handleClose = () => {
    setSelectedRole('');
    setMenuPermissions({});
    setFunctionalityPermissions({});
    setNewMenuOption('');
    setNewFunctionalityOption('');
    setMenuOptionsList([...defaultMenuOptions]);
    setFunctionalityList([...defaultFunctionalityOptions]);
    onClose();
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (!selectedRole) {
      return;
    }

    // Get custom options (those not in default lists)
    const customMenuOptions = menuOptionsList.filter((opt) => !defaultMenuOptions.includes(opt));
    const customFunctionalityOptions = functionalityList.filter((opt) => !defaultFunctionalityOptions.includes(opt));

    // Build full permissions objects with explicit Allow/Deny for all items
    const fullMenuPermissions = {};
    menuOptionsList.forEach((item) => {
      fullMenuPermissions[item] = !!menuPermissions[item]; // true = Allow, false = Deny
    });

    const fullFunctionalityPermissions = {};
    functionalityList.forEach((item) => {
      fullFunctionalityPermissions[item] = !!functionalityPermissions[item]; // true = Allow, false = Deny
    });

    // Build the access_json structure
    const accessJson = {
      menuPermissions: fullMenuPermissions,
      functionalityPermissions: fullFunctionalityPermissions,
      customMenuOptions: customMenuOptions.length > 0 ? customMenuOptions : undefined,
      customFunctionalityOptions: customFunctionalityOptions.length > 0 ? customFunctionalityOptions : undefined,
    };

    onSubmit({
      role_id: selectedRole,
      access_json: accessJson,
    });
  };

  // Render items grid (3 columns) - checkboxes (checked = Allow, unchecked = Deny)
  const renderItemsGrid = (items, permissions, onToggle) => {
    return (
      <div className="grid grid-cols-3 gap-y-3 gap-x-6 mt-4 px-2">
        {items.map((item) => (
          <label
            key={item}
            className="flex items-center gap-2 cursor-pointer select-none group"
          >
            <input
              type="checkbox"
              checked={!!permissions[item]}
              onChange={() => onToggle(item)}
              className="w-4 h-4 accent-secondary-700 cursor-pointer"
            />
            <span
              className={`text-sm ${
                permissions[item]
                  ? 'font-semibold text-gray-900'
                  : 'text-gray-500'
              } group-hover:text-gray-900`}
            >
              {item}
            </span>
          </label>
        ))}
      </div>
    );
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
        <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 w-full md:w-[700px] max-w-3xl max-h-[90vh] overflow-y-auto border border-gray-200 relative">
          {/* Close X Button */}
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold p-1"
            aria-label="Close"
          >
            &times;
          </button>

          {/* Modal Title */}
          <div className="text-gray-500 text-sm mb-2">
            {isEditMode ? 'Edit Permissions' : 'Add Permissions'}
          </div>

          <form onSubmit={handleFormSubmit}>
            {/* Role Selection */}
            <div className="bg-gray-100 rounded-lg px-4 py-3 mb-6">
              <div className="flex items-center gap-2">
                <span className="text-gray-700 font-medium">User Role :</span>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="bg-transparent outline-none text-gray-800 font-semibold"
                  required
                >
                  <option value="">Select Role</option>
                  {roleOptions.map((role) => (
                    <option key={role.role_id || role.unique_id} value={role.role_id || role.unique_id}>
                      {role.role}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Permissions Section */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-gray-800">Permissions</h3>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-sm">Add</span>
                  <input
                    type="text"
                    value={newMenuOption}
                    onChange={(e) => setNewMenuOption(e.target.value)}
                    placeholder=""
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-36 outline-none focus:border-secondary-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddMenuOption}
                    disabled={!newMenuOption.trim()}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                      newMenuOption.trim()
                        ? 'bg-secondary-700 text-white hover:bg-secondary-800'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Menu Options Header */}
              <div className="bg-gray-100 rounded-lg px-4 py-3">
                <span className="text-gray-700 font-medium">Menu Options : Controls</span>
              </div>

              {/* Menu Items Grid */}
              {renderItemsGrid(menuOptionsList, menuPermissions, toggleMenuPermission)}
            </div>

            {/* Functionality Section */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-gray-800">Functionality</h3>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-sm">Add</span>
                  <input
                    type="text"
                    value={newFunctionalityOption}
                    onChange={(e) => setNewFunctionalityOption(e.target.value)}
                    placeholder=""
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-36 outline-none focus:border-secondary-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddFunctionalityOption}
                    disabled={!newFunctionalityOption.trim()}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                      newFunctionalityOption.trim()
                        ? 'bg-secondary-700 text-white hover:bg-secondary-800'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Functionality Header */}
              <div className="bg-gray-100 rounded-lg px-4 py-3">
                <span className="text-gray-700 font-medium">Functionality Access Controls</span>
              </div>

              {/* Functionality Items Grid */}
              {renderItemsGrid(functionalityList, functionalityPermissions, toggleFunctionalityPermission)}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="border border-secondary-700 text-secondary-700 py-2 px-6 rounded-full hover:bg-gray-50 transition"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !selectedRole}
                className="bg-secondary-700 text-white py-2 px-8 rounded-full shadow hover:bg-secondary-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default PermissionsModal;
